import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { runsAPI } from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import { ProjectRowSkeleton } from '../components/Skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.jsx';
import { PlusCircle, Search, Loader2, FolderKanban, SearchX } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_FILTERS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'running', label: 'En cours' },
  { value: 'completed', label: 'Terminés' },
  { value: 'failed', label: 'Échoués' },
  { value: 'pending', label: 'En attente' },
  { value: 'cancelled', label: 'Annulés' },
];

export default function ProjectsPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadRuns();
  }, []);

  const loadRuns = async () => {
    try {
      const res = await runsAPI.list(100, 0);
      setRuns(res.data || []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return runs.filter((r) => {
      const matchesSearch = !search || r.goal?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [runs, search, statusFilter]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => navigate('/projects/new')} className="glow-primary">
          <PlusCircle className="w-4 h-4 mr-2" />
          Nouveau projet
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="divide-y divide-border">
              <div className="grid grid-cols-12 gap-4 px-5 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/30">
                <div className="col-span-5">Projet</div>
                <div className="col-span-2">Stack</div>
                <div className="col-span-2">Statut</div>
                <div className="col-span-1 text-right">Étapes</div>
                <div className="col-span-2 text-right">Date</div>
              </div>
              {[...Array(5)].map((_, i) => <ProjectRowSkeleton key={i} />)}
            </div>
          ) : filtered.length === 0 ? (
            runs.length === 0 ? (
              <EmptyState
                icon={FolderKanban}
                title="Aucun projet"
                description="Lancez votre premier projet pour commencer."
                actionLabel="Nouveau projet"
                onAction={() => navigate('/projects/new')}
              />
            ) : (
              <EmptyState
                icon={SearchX}
                title="Aucun résultat"
                description="Aucun projet ne correspond à vos filtres."
              />
            )
          ) : (
            <div className="divide-y divide-border">
              {/* Header */}
              <div className="grid grid-cols-12 gap-4 px-5 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/30">
                <div className="col-span-5">Projet</div>
                <div className="col-span-2">Stack</div>
                <div className="col-span-2">Statut</div>
                <div className="col-span-1 text-right">Étapes</div>
                <div className="col-span-2 text-right">Date</div>
              </div>

              {/* Rows */}
              {filtered.map((run, idx) => (
                <button
                  key={run.id}
                  onClick={() => navigate(`/projects/${run.id}`)}
                  className="grid grid-cols-12 gap-4 px-5 py-3 w-full hover:bg-accent/50 transition-colors text-left items-center animate-fade-in"
                  style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'both' }}
                >
                  <div className="col-span-5 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {run.goal?.substring(0, 80) || 'Sans titre'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {run.id.substring(0, 8)}...
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Badge variant="outline" className="text-xs capitalize">
                      {run.stack}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <StatusBadge status={run.status} size="sm" />
                  </div>
                  <div className="col-span-1 text-right text-sm text-muted-foreground">
                    {run.current_step || 0}/{run.max_steps || 20}
                  </div>
                  <div className="col-span-2 text-right text-xs text-muted-foreground">
                    {run.created_at
                      ? formatDistanceToNow(new Date(run.created_at), { addSuffix: true, locale: fr })
                      : '-'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
